import React, {useState, useContext, useCallback, useEffect} from 'react';
import {debounce} from 'lodash';
import {resource} from '@albert-io/json-api-framework/request/builder';

import {Button, Icon, LoadingSpinnerLong, NoDataMsg, SearchInput} from '@albert-io/atomic';
import {pushQueryParams, removeQueryParams} from 'client/history';

import {ReportsContext} from '../Reports.context';
import Drilldown from '../Drilldown/Drilldown.react';

import Filters from './Filters/Filters.react';
import Metrics from './Metrics/Metrics.react';
import ReportsTable from './Table/ReportsTable.react';
import ReportsExport from './Export/ReportsExport.react';
import GradebookTable from './GradebookTable/GradebookTable.react';

import './report.scss';

const debouncedOnSearch = debounce((searchStr) => {
  pushQueryParams({s: searchStr});
}, 500);

function Report() {
  const {
    data,
    topLevelFilterType,
    topLevelFilterId,
    start,
    end,
    dimensions,
    keyword,
    metrics,
    path,
    loading,
    loadingMore,
    error,
    variables,
    totals,
    fetchMoreData,
    sort,
    limit,
    page
  } = useContext(ReportsContext);
  const [fetchedResource, setFetchedResource] = useState(null);

  /* We make a copy of the path array that we can freely mutate */
  const breadcrumbTrail = [...path].filter(
    /*
      special case - gradebook drilldowns don't follow the "normal" pattern since there's
      no ID to drilldown into. Really the only difference between a gradebook drilldown and a
      "normal" report is that we show "Gradebook" in the breadcrumb, but the data is the same
      as if we got here from a "normal" drilldown. So we can just remove "gradebook" from the path
      for the purpose of this logic
    */
    (item, i, arr) => item !== 'gradebook' || i === arr.length - 1
  );
  const nonBreadcrumbItems = breadcrumbTrail.slice(breadcrumbTrail.length - 2);
  const [dimensionInFocusId, defaultValue] = nonBreadcrumbItems;
  const dimensionInFocus = breadcrumbTrail[breadcrumbTrail.length - 3];

  // fetch data to render in BreadcrumbHeadings
  const fetchResource = useCallback(async () => {
    let queryData;
    switch (dimensionInFocus) {
      case 'assignments':
        queryData = await resource('assignments_v3')
          .mandarkEndpoint(['assignments_v3', dimensionInFocusId])
          .include('classrooms_v1')
          .getResourcePromise();
        break;
      case 'questions':
        queryData = await resource('questions_v3')
          .mandarkEndpoint(['questions_v3', dimensionInFocusId])
          .getResourcePromise();
        break;
      case 'standards':
        queryData = await resource('standards_v1')
          .mandarkEndpoint(['standards_v1', dimensionInFocusId])
          .getResourcePromise();
        break;
      case 'students':
        queryData = await resource('students_v2')
          .mandarkEndpoint(['students_v2', dimensionInFocusId])
          .getResourcePromise();
        break;
      default:
    }
    setFetchedResource(queryData);
  }, [dimensionInFocus, dimensionInFocusId]);

  useEffect(() => {
    if (dimensionInFocus && dimensionInFocusId) {
      fetchResource();
    }
  }, [dimensionInFocus, dimensionInFocusId, fetchResource]);

  const hasAppliedDateRange = start && end;
  const shouldSkipRendering = !topLevelFilterId || !dimensions.length;

  if (shouldSkipRendering) {
    return null;
  }

  const isMultiDimensional = dimensions.length > 1;

  return (
    <>
      <Drilldown
        defaultValue={defaultValue}
        dimensionInFocus={dimensionInFocus}
        headingData={fetchedResource}
      />
      <div className='reports__toolbar u-display_flex u-justify-content_space-between u-pad-tb_3 u-pad-lr_2'>
        <Filters />
        <div className='reports__search-export'>
          <Metrics />
          {isMultiDimensional ? null : (
            <SearchInput
              className='u-mar-l_auto'
              onChange={(e) => debouncedOnSearch(e.target.value)}
              onReset={() => removeQueryParams('s')}
              placeholder='Search'
              defaultValue={keyword}
            />
          )}
          {!data ? (
            <Button color='secondary' variant='outlined' className='u-white-space_nowrap' disabled>
              <Icon className='u-mar-r_1' icon='download' />
              Download Report
            </Button>
          ) : (
            <ReportsExport disabled={loading} metrics={metrics} path={path} />
          )}
        </div>
      </div>

      {/* gradebook loading is internal so only show if not multidimensional */}
      {hasAppliedDateRange && (loading || !data) && !isMultiDimensional && (
        <LoadingSpinnerLong size={2} className='u-mar-t_2' />
      )}

      {hasAppliedDateRange && (
        <div className='u-mar-b_4 reports-table u-pad-lr_2'>
          {isMultiDimensional && topLevelFilterType !== 'classrooms' && (
            <div className='u-display_flex u-justify-content_center'>
              <NoDataMsg.Container centered>
                <NoDataMsg.Heading>Select an individual classroom</NoDataMsg.Heading>
                <NoDataMsg.Body>
                  {/* TODO potentially change this copy for schools and districts */}
                  This report is not supported for all classrooms. Select a specific class to use
                  this report.
                </NoDataMsg.Body>
              </NoDataMsg.Container>
            </div>
          )}

          {isMultiDimensional && topLevelFilterType === 'classrooms' && (
            <GradebookTable
              data={data}
              metricColumns={metrics}
              dimensions={dimensions}
              variables={variables}
              loading={loading}
              loadingMore={loadingMore}
              loadMore={() => {
                fetchMoreData(null);
              }}
              totals={totals}
              sorts={sort}
              limit={limit}
              page={page}
              topLevelFilterType={topLevelFilterType}
            />
          )}

          {!isMultiDimensional && data && !loading && (
            <ReportsTable
              loading={loading}
              data={data}
              dimension={dimensions[0]}
              metrics={metrics}
            />
          )}

          {error ? 'Error occured!' : null}
        </div>
      )}
    </>
  );
}

export default Report;
